import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Fab, Fade, IconButton, TextField, Typography, Zoom } from "@mui/material";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import { toast } from "react-toastify";
import {
  useSendChatMessageMutation,
  useConfirmChatEntityMutation,
} from "../../../slice/chatbotApiSlice";
import { useCurrentUser } from "../../../hooks/useCurrentUser";
import ChatMessage from "./ChatMessage";
import ChatLoadingIndicator from "./ChatLoadingIndicator";
import { CHATBOT_TEAL } from "./chatbotFormat";

let nextMessageId = 1;
const makeId = () => nextMessageId++;

const WELCOME_MESSAGE = {
  id: makeId(),
  role: "bot",
  status: "done",
  text: "Hi! Ask me anything about your projects, tasks, milestones or timesheets.",
  timestamp: new Date(),
};

const mapChatResponseToMessage = (response) => ({
  id: makeId(),
  role: "bot",
  status: response.status,
  text: response.answer,
  params: response.sql_params,
  data: response.data,
  rowCount: response.row_count,
  confirmationOptions: response.confirmation_options,
  sessionId: response.session_id,
});

const Chatbot = () => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const scrollRef = useRef(null);

  const [sendChatMessage, { isLoading: isSending }] = useSendChatMessageMutation();
  const [confirmChatEntity, { isLoading: isConfirming }] = useConfirmChatEntityMutation();
  const isBusy = isSending || isConfirming;

  // Identity comes from the API-backed Redux user, not editable localStorage.
  const { id: currentUserId, role: loginRole } = useCurrentUser();
  const currentUserRole = loginRole ? loginRole.toUpperCase() : null;
  const isAllowedRole = currentUserRole === "ADMIN" || currentUserRole === "MANAGER";

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isBusy]);

  // Stamped client-side only — there's no backend/DB persistence for chat history to read a time from.
  const appendMessage = (message) =>
    setMessages((prev) => [...prev, { timestamp: new Date(), ...message }]);

  // Pairs up consecutive user->bot messages into {query, answer} turns, most recent 3 —
  // sent with each request so the backend can resolve follow-ups ("what about last month?")
  // without the user having to repeat the project/person every time.
  const buildHistory = (msgs) => {
    const turns = [];
    for (let i = 0; i < msgs.length - 1; i++) {
      if (msgs[i].role === "user" && msgs[i + 1].role === "bot") {
        turns.push({ query: msgs[i].text, answer: msgs[i + 1].text });
      }
    }
    return turns.slice(-3);
  };

  const handleSend = async () => {
    const query = input.trim();
    if (!query || isBusy) return;

    const userId = currentUserId ? Number(currentUserId) : null;
    const role = currentUserRole;
    if (!userId || !role) {
      toast.error("Could not identify the logged-in user. Please log in again.");
      return;
    }

    const history = buildHistory(messages);
    appendMessage({ id: makeId(), role: "user", text: query });
    setInput("");

    try {
      const response = await sendChatMessage({ query, user_id: userId, role, history }).unwrap();
      appendMessage(mapChatResponseToMessage(response));
    } catch (err) {
      appendMessage({
        id: makeId(),
        role: "bot",
        status: "error",
        text: "Something went wrong while reaching the chatbot service. Please try again.",
      });
    }
  };

  const handleSelectConfirmation = async (messageId, sessionId, entityType, option) => {
    if (!sessionId || isBusy) return;

    // Lock the buttons on the message that was just answered.
    setMessages((prev) =>
      prev.map((m) => (m.id === messageId ? { ...m, confirmationResolved: true } : m))
    );
    appendMessage({ id: makeId(), role: "user", text: option.name });

    try {
      const response = await confirmChatEntity({
        session_id: sessionId,
        entity_type: entityType,
        entity_id: option.id,
        entity_name: option.name,
      }).unwrap();
      appendMessage(mapChatResponseToMessage(response));
    } catch (err) {
      appendMessage({
        id: makeId(),
        role: "bot",
        status: "error",
        text: "Something went wrong while confirming your selection. Please try again.",
      });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isAllowedRole) return null;

  return (
    <>
      <Zoom in={!open}>
        <Fab
          onClick={() => setOpen(true)}
          sx={{
            position: "fixed",
            bottom: 24,
            right: 24,
            zIndex: 1300,
            backgroundColor: CHATBOT_TEAL,
            color: "#fff",
            "&:hover": { backgroundColor: CHATBOT_TEAL, opacity: 0.9 },
          }}
        >
          <SmartToyOutlinedIcon />
        </Fab>
      </Zoom>

      <Zoom in={open}>
        <Box
          sx={{
            position: "fixed",
            bottom: 24,
            right: 24,
            width: 384,
            maxWidth: "calc(100vw - 32px)",
            height: 560,
            maxHeight: "calc(100vh - 48px)",
            display: "flex",
            flexDirection: "column",
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow: "0 8px 30px rgba(0,0,0,0.25)",
            backgroundColor: "var(--main-bg-color)",
            border: "1px solid var(--border-color)",
            zIndex: 1300,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "12px 16px",
              backgroundColor: CHATBOT_TEAL,
              color: "#fff",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <SmartToyOutlinedIcon fontSize="small" />
              <Typography sx={{ fontSize: "15px", fontWeight: 600 }}>PMS Assistant (Beta)</Typography>
            </Box>
            <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: "#fff" }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Messages */}
          <Box
            ref={scrollRef}
            sx={{
              flexGrow: 1,
              overflowY: "auto",
              padding: "14px",
              backgroundColor: "var(--main-bg-color)",
            }}
          >
            {messages.map((message) => (
              <ChatMessage
                key={message.id}
                message={message}
                onSelectConfirmation={handleSelectConfirmation}
              />
            ))}
            {isBusy && (
              <Fade in timeout={300}>
                <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 1.5 }}>
                  <Box
                    sx={{
                      borderRadius: "14px",
                      padding: "9px 14px",
                      backgroundColor: "var(--cell-bg-color)",
                      border: "1px solid var(--border-color)",
                    }}
                  >
                    <ChatLoadingIndicator />
                  </Box>
                </Box>
              </Fade>
            )}
          </Box>

          {/* Input */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              padding: "10px",
              borderTop: "1px solid var(--border-color)",
              backgroundColor: "var(--cell-bg-color)",
            }}
          >
            <TextField
              fullWidth
              multiline
              maxRows={4}
              size="small"
              placeholder="Ask about a project, task, milestone…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isBusy}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "var(--main-bg-color)",
                  color: "var(--text-color)",
                  fontSize: "13.5px",
                },
              }}
            />
            <IconButton
              onClick={handleSend}
              disabled={isBusy || !input.trim()}
              sx={{
                width: 36,
                height: 36,
                backgroundColor: CHATBOT_TEAL,
                color: "#fff",
                transition: "transform 0.15s ease, box-shadow 0.2s ease, background-color 0.2s ease",
                boxShadow: !isBusy && input.trim() ? "0 2px 10px rgba(0,0,0,0.22)" : "none",
                "&:hover": {
                  backgroundColor: CHATBOT_TEAL,
                  opacity: 0.9,
                  transform: !isBusy && input.trim() ? "scale(1.08)" : "none",
                },
                "&:active": {
                  transform: !isBusy && input.trim() ? "scale(0.9)" : "none",
                },
                "&.Mui-disabled": {
                  backgroundColor: CHATBOT_TEAL,
                  color: "#fff",
                  opacity: 0.4,
                  boxShadow: "none",
                },
              }}
            >
              {isBusy ? <CircularProgress size={16} color="inherit" /> : <SendIcon fontSize="small" />}
            </IconButton>
          </Box>
        </Box>
      </Zoom>
    </>
  );
};

export default Chatbot;
